import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend, CartesianGrid, LabelList, PieChart, Pie } from "recharts";
import { FileText, FileDown } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "@/context/AuthContext";
import { exportNodeToPDF, bizSlug } from "@/lib/pdfPage";
import { PrintHeader } from "@/components/PrintHeader";
import { toast } from "sonner";

const palette = ["#0F172A", "#10B981", "#EF4444", "#F59E0B", "#8B5CF6", "#0EA5E9", "#EC4899", "#6B7280"];

export default function Reports() {
  const { currency, fmt } = useAuth();
  const formatPKR = fmt;
  const CUR_SYM = currency?.symbol || "₨";
  const CUR_CODE = currency?.code || "PKR";

  const compactPKR = (n) => {
    const num = Number(n || 0);
    if (num === 0) return "";
    const abs = Math.abs(num);
    if (abs >= 1_000_000) return `${CUR_SYM}${(num / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${CUR_SYM}${(num / 1_000).toFixed(1)}k`;
    return `${CUR_SYM}${num.toFixed(0)}`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white border border-[#E5E7EB] px-3 py-2 text-xs">
        <div className="font-semibold text-[#0F172A] mb-1">{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="w-2 h-2 inline-block" style={{ background: p.color }} />
            <span className="text-[#4B5563]">{p.name}:</span>
            <span className="font-mono font-semibold text-[#111827]">{formatPKR(p.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  const [monthly, setMonthly] = useState([]);
  const [cats, setCats] = useState([]);
  const [parties, setParties] = useState([]);
  const [dues, setDues] = useState({ rows: [], total_receivable: 0, total_payable: 0, net_dues: 0 });
  const [heads, setHeads] = useState([]);
  const [biz, setBiz] = useState(null);
  const [dateRange, setDateRange] = useState({ start_date: "", end_date: "" });
  const [exportingPdf, setExportingPdf] = useState(false);
  const printRef = useRef(null);

  const handleDownloadPDF = async () => {
    if (!printRef.current) return;
    setExportingPdf(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      const filename = `${bizSlug(biz?.name)}-reports-${date}.pdf`;
      // Same flow as Dashboard: A4 portrait, full usable width fit, 0.5" margins,
      // multi-page vertical pagination. The "Net flow per party" section is
      // marked data-pdf-hide and removed during capture.
      await exportNodeToPDF(printRef.current, filename, { orientation: "portrait" });
      toast.success("Reports PDF downloaded");
    } catch (e) {
      toast.error("Failed to generate PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  useEffect(() => {
    api.get("/business/me").then((r) => setBiz(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = {};
    if (dateRange.start_date) params.start_date = dateRange.start_date;
    if (dateRange.end_date) params.end_date = dateRange.end_date;
    Promise.all([
      api.get("/reports/monthly", { params }),
      api.get("/reports/categories", { params }),
      api.get("/reports/parties", { params }),
      api.get("/reports/dues", { params }),
      api.get("/reports/account-heads", { params }),
    ]).then(([m, c, p, d, h]) => {
      setMonthly(m.data); setCats(c.data); setParties(p.data); setDues(d.data); setHeads(h.data);
    });
  }, [dateRange.start_date, dateRange.end_date]);

  const debitCats = useMemo(
    () => cats.filter((c) => c.debit > 0)
              .map((c) => ({ ...c, value: c.debit }))
              .sort((a, b) => b.value - a.value),
    [cats]
  );

  const resetDates = () => setDateRange({ start_date: "", end_date: "" });

  const exportPL = () => {
    // P&L = Income (Revenue, Other Income heads — credits) − Expenses (everything else — debits)
    // Group by account_head; net flow = credit - debit per head.
    const INCOME_HEADS = new Set(["Revenue", "Other Income"]);
    const incomeHeads = heads.filter((h) => INCOME_HEADS.has(h.account_head));
    const expenseHeads = heads.filter((h) => !INCOME_HEADS.has(h.account_head));
    const totalIncome = incomeHeads.reduce((s, h) => s + h.credit, 0);
    const totalExpense = expenseHeads.reduce((s, h) => s + h.debit, 0);
    const netPL = totalIncome - totalExpense;

    const doc = new jsPDF("p", "mm", "a4");
    const W = doc.internal.pageSize.getWidth();
    // 0.5 inch margins = 12.7mm on every side
    const MARGIN = 12.7;
    const tableMargins = { left: MARGIN, right: MARGIN };

    // Header
    doc.setFontSize(16); doc.setFont("helvetica", "bold");
    doc.text(biz?.name || "Cashflow Ledger", W / 2, MARGIN + 5, { align: "center" });
    const subParts = [];
    if (biz?.address) subParts.push(biz.address);
    const cityCountry = [biz?.city, biz?.country].filter(Boolean).join(", ");
    if (cityCountry) subParts.push(cityCountry);
    if (biz?.phone) subParts.push(`Phone: ${biz.phone}`);
    if (biz?.email) subParts.push(biz.email);
    if (subParts.length) {
      doc.setFontSize(8); doc.setFont("helvetica", "normal");
      doc.text(subParts.join("  ·  "), W / 2, MARGIN + 10, { align: "center" });
    }
    doc.setFontSize(13); doc.setFont("helvetica", "bold");
    doc.text("Profit & Loss Statement", W / 2, MARGIN + 19, { align: "center" });
    const periodLine = dateRange.start_date || dateRange.end_date
      ? `Period: ${dateRange.start_date || "—"} to ${dateRange.end_date || "—"}`
      : `All-time · Generated ${new Date().toLocaleDateString()}`;
    doc.setFontSize(8); doc.setFont("helvetica", "normal");
    doc.text(periodLine, W / 2, MARGIN + 24, { align: "center" });

    let y = MARGIN + 32;
    // Number-only formatter — column header already includes currency code,
    // and the unicode currency symbol (₨, €, etc.) doesn't render in jsPDF's default Helvetica.
    const fmtNum = (n) => Number(n || 0).toLocaleString(currency?.locale || "en-PK", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // Income section
    autoTable(doc, {
      startY: y,
      margin: tableMargins,
      head: [["INCOME", `Amount (${CUR_CODE})`]],
      body: [
        ...incomeHeads.map((h) => [h.account_head, fmtNum(h.credit)]),
        [{ content: "Total Income", styles: { fontStyle: "bold" } }, { content: fmtNum(totalIncome), styles: { fontStyle: "bold", halign: "right" } }],
      ],
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: { fillColor: [16, 185, 129], textColor: 255 },
      columnStyles: { 1: { halign: "right" } },
      theme: "grid",
    });
    y = doc.lastAutoTable.finalY + 6;

    autoTable(doc, {
      startY: y,
      margin: tableMargins,
      head: [["EXPENSES", `Amount (${CUR_CODE})`]],
      body: [
        ...expenseHeads.map((h) => [h.account_head, fmtNum(h.debit)]),
        [{ content: "Total Expenses", styles: { fontStyle: "bold" } }, { content: fmtNum(totalExpense), styles: { fontStyle: "bold", halign: "right" } }],
      ],
      styles: { fontSize: 9, cellPadding: 2.5 },
      headStyles: { fillColor: [239, 68, 68], textColor: 255 },
      columnStyles: { 1: { halign: "right" } },
      theme: "grid",
    });
    y = doc.lastAutoTable.finalY + 8;

    // Net P&L — render the amount as ASCII (currency CODE + number) so jsPDF's Helvetica
    // doesn't garble the unicode currency symbol.
    doc.setFontSize(12); doc.setFont("helvetica", "bold");
    const label = netPL >= 0 ? "NET PROFIT" : "NET LOSS";
    const color = netPL >= 0 ? [16, 185, 129] : [239, 68, 68];
    doc.setTextColor(...color);
    doc.text(label, MARGIN, y);
    doc.text(`${CUR_CODE} ${fmtNum(Math.abs(netPL))}`, W - MARGIN, y, { align: "right" });
    doc.setTextColor(0, 0, 0);

    doc.save(`${bizSlug(biz?.name)}-PL-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <div className="p-10 max-w-7xl" data-testid="reports-page">
      <PageHeader overline="Analytics" title="Reports & Insights" testid="reports-header">
        <div className="flex items-center gap-2">
          <Button onClick={handleDownloadPDF} disabled={exportingPdf} data-testid="reports-download-pdf" variant="outline"
            className="rounded-none border-[#0F172A] h-10 text-xs uppercase tracking-wider font-semibold">
            <FileDown className="h-3.5 w-3.5 mr-2" /> {exportingPdf ? "Generating…" : "Download PDF"}
          </Button>
          <Button onClick={exportPL} data-testid="export-pl-pdf-button" variant="outline"
            className="rounded-none border-[#0F172A] h-10 text-xs uppercase tracking-wider font-semibold">
            <FileText className="h-3.5 w-3.5 mr-2" /> P&L PDF
          </Button>
        </div>
      </PageHeader>

      <div ref={printRef} className="bg-white" data-testid="reports-print-region">
        {/* Print-only header — shown only inside the PDF capture */}
        <div style={{ display: "none" }} className="mb-4" data-pdf-only="true">
          <PrintHeader business={biz} title="Reports & Insights"
            subtitle={dateRange.start_date || dateRange.end_date
              ? `Period: ${dateRange.start_date || "earliest"} → ${dateRange.end_date || "today"}`
              : "Period: All time"} />
        </div>

      {/* Date filter */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 p-5 border border-[#E5E7EB] bg-[#F9FAFB]" data-pdf-hide="true" data-testid="reports-filter-bar">
        <div>
          <Label className="overline mb-1.5 block">From</Label>
          <Input type="date" value={dateRange.start_date}
            onChange={(e) => setDateRange({ ...dateRange, start_date: e.target.value })}
            data-testid="report-start-date" className="rounded-none h-10 border-[#E5E7EB] bg-white" />
        </div>
        <div>
          <Label className="overline mb-1.5 block">To</Label>
          <Input type="date" value={dateRange.end_date}
            onChange={(e) => setDateRange({ ...dateRange, end_date: e.target.value })}
            data-testid="report-end-date" className="rounded-none h-10 border-[#E5E7EB] bg-white" />
        </div>
        <div className="flex items-end">
          <Button onClick={resetDates} variant="outline" data-testid="report-reset-dates"
            className="rounded-none h-10 border-[#E5E7EB] bg-white text-xs uppercase tracking-wider font-semibold">
            Reset
          </Button>
        </div>
        <div className="flex items-end justify-end text-xs text-[#6B7280]" data-testid="report-range-summary">
          {dateRange.start_date || dateRange.end_date
            ? `Showing ${dateRange.start_date || "earliest"} → ${dateRange.end_date || "today"}`
            : "Showing all time"}
        </div>
      </div>

      {/* Chart of Accounts + overall Credit vs Debit doughnut, side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-[10px] mb-8">
      {/* Net flow per party — full width */}
      <section className="border border-[#E5E7EB] bg-white lg:col-span-2" data-testid="account-heads-section">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between gap-4">
          <div>
            <div className="overline mb-1">Chart of Accounts</div>
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">By Account Head</h3>
          </div>
          <div className="text-right text-xs text-[#6B7280] max-w-xs">
            Set Account Head on each category to group them here. Entries inherit their head via category.
          </div>
        </div>
        <div className="p-6 h-96" data-pdf-page="account-heads-chart">
          {heads.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-[#6B7280]" data-testid="heads-empty">
              No entries in this range.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={heads} margin={{ top: 28, right: 16, left: 0, bottom: 60 }}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="account_head" stroke="#6B7280" fontSize={11} interval={0} angle={-20} textAnchor="end" height={70} />
                <YAxis stroke="#6B7280" fontSize={11} tickFormatter={compactPKR} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "#F3F4F6" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="credit" name="Credit" fill="#10B981">
                  <LabelList dataKey="credit" position="top" formatter={compactPKR} fontSize={10} fontWeight={600} fill="#10B981" />
                </Bar>
                <Bar dataKey="debit" name="Debit" fill="#EF4444">
                  <LabelList dataKey="debit" position="top" formatter={compactPKR} fontSize={10} fontWeight={600} fill="#EF4444" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        {heads.length > 0 && (
          <div className="border-t border-[#E5E7EB]" data-pdf-page="account-heads-table">
            <table className="w-full text-sm" data-testid="heads-table">
              <thead>
                <tr className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                  <th className="text-left px-6 py-2 overline">Account Head</th>
                  <th className="text-right px-4 py-2 overline text-[#10B981]">Credit</th>
                  <th className="text-right px-4 py-2 overline text-[#EF4444]">Debit</th>
                  <th className="text-right px-4 py-2 overline">Net</th>
                  <th className="text-right px-6 py-2 overline">Entries</th>
                </tr>
              </thead>
              <tbody>
                {heads.map((h) => (
                  <tr key={h.account_head} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`head-row-${h.account_head}`}>
                    <td className="px-6 py-2 font-medium text-[#111827]">{h.account_head}</td>
                    <td className="px-4 py-2 text-right font-mono text-[#10B981]">{h.credit > 0 ? formatPKR(h.credit) : <span className="text-[#D1D5DB]">—</span>}</td>
                    <td className="px-4 py-2 text-right font-mono text-[#EF4444]">{h.debit > 0 ? formatPKR(h.debit) : <span className="text-[#D1D5DB]">—</span>}</td>
                    <td className={`px-4 py-2 text-right font-mono font-semibold ${h.net >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                      {h.net >= 0 ? "+" : "−"} {formatPKR(Math.abs(h.net))}
                    </td>
                    <td className="px-6 py-2 text-right text-xs text-[#6B7280] font-mono">{h.entries_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Overall Credit vs Debit doughnut — sits beside Chart of Accounts */}
      <section className="border border-[#E5E7EB] bg-white lg:col-span-1 flex flex-col" data-testid="credit-debit-doughnut-section">
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <div className="overline mb-1">Cash Flow Mix</div>
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Credit vs Debit</h3>
        </div>
        {(() => {
          const totalCredit = heads.reduce((s, h) => s + (h.credit || 0), 0);
          const totalDebit = heads.reduce((s, h) => s + (h.debit || 0), 0);
          const grand = totalCredit + totalDebit;
          const net = totalCredit - totalDebit;
          const pct = (v) => grand > 0 ? ((v / grand) * 100).toFixed(1) : "0.0";
          const data = [
            { name: "Credit", value: totalCredit, color: "#10B981" },
            { name: "Debit", value: totalDebit, color: "#EF4444" },
          ];
          if (grand === 0) {
            return (
              <div className="flex-1 flex items-center justify-center text-sm text-[#6B7280] p-6 h-96" data-testid="doughnut-empty">
                No entries in this range.
              </div>
            );
          }
          return (
            <>
              <div className="p-6 h-96 relative" data-testid="doughnut-chart-wrapper">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data}
                      dataKey="value"
                      nameKey="name"
                      innerRadius="58%"
                      outerRadius="85%"
                      paddingAngle={2}
                      stroke="#fff"
                      strokeWidth={2}
                    >
                      {data.map((d) => <Cell key={d.name} fill={d.color} />)}
                    </Pie>
                    <Tooltip
                      formatter={(value, name) => [`${formatPKR(value)} · ${pct(value)}%`, name]}
                      contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 12 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Centered net label overlay */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none" data-testid="doughnut-center">
                  <div className="overline text-[10px]">Net</div>
                  <div className={`font-display text-xl font-bold tracking-tight ${net >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`} data-testid="doughnut-net">
                    {net >= 0 ? "+" : "−"} {formatPKR(Math.abs(net))}
                  </div>
                </div>
              </div>
              <div className="border-t border-[#E5E7EB] divide-y divide-[#E5E7EB]" data-testid="doughnut-legend">
                <div className="flex items-center justify-between px-6 py-3" data-testid="doughnut-credit-row">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 inline-block" style={{ background: "#10B981" }} />
                    <span className="text-sm font-medium text-[#111827]">Credit</span>
                    <span className="text-xs text-[#6B7280] font-mono">({pct(totalCredit)}%)</span>
                  </div>
                  <div className="font-mono text-sm font-semibold text-[#10B981]">{formatPKR(totalCredit)}</div>
                </div>
                <div className="flex items-center justify-between px-6 py-3" data-testid="doughnut-debit-row">
                  <div className="flex items-center gap-2">
                    <span className="w-3 h-3 inline-block" style={{ background: "#EF4444" }} />
                    <span className="text-sm font-medium text-[#111827]">Debit</span>
                    <span className="text-xs text-[#6B7280] font-mono">({pct(totalDebit)}%)</span>
                  </div>
                  <div className="font-mono text-sm font-semibold text-[#EF4444]">{formatPKR(totalDebit)}</div>
                </div>
              </div>
            </>
          );
        })()}
      </section>
      </div>

      {/* Monthly Cash Flow */}
      <section className="border border-[#E5E7EB] bg-white mb-8" data-testid="monthly-chart-section">
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <div className="overline mb-1">Monthly Cash Flow</div>
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Credit vs Debit by month</h3>
        </div>
        <div className="p-6 h-80">
          {monthly.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-[#6B7280]" data-testid="monthly-empty">No data yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly} margin={{ top: 24, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="month" stroke="#6B7280" fontSize={11} />
                <YAxis stroke="#6B7280" fontSize={11} tickFormatter={compactPKR} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "#F9FAFB" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="credit" fill="#10B981" name="Credit">
                  <LabelList dataKey="credit" position="top" formatter={compactPKR} fill="#10B981" fontSize={10} fontWeight={600} />
                </Bar>
                <Bar dataKey="debit" fill="#EF4444" name="Debit">
                  <LabelList dataKey="debit" position="top" formatter={compactPKR} fill="#EF4444" fontSize={10} fontWeight={600} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* Total volume per category — full width */}
      <section className="border border-[#E5E7EB] bg-white mb-8" data-testid="categories-chart-section">
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <div className="overline mb-1">By Category</div>
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Total volume per category</h3>
        </div>
        <div className="p-6 h-80">
          {cats.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-[#6B7280]" data-testid="cats-empty">No data.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={cats} margin={{ top: 28, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={11} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis stroke="#6B7280" fontSize={11} tickFormatter={compactPKR} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "#F9FAFB" }} />
                <Bar dataKey="total" name="Total">
                  {cats.map((c) => <Cell key={c.name} fill={c.color || "#0F172A"} />)}
                  <LabelList dataKey="total" position="top" formatter={compactPKR} fontSize={11} fontWeight={600} fill="#111827" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* Debits by Category — debit-only column chart */}
      <section className="border border-[#E5E7EB] bg-white mb-8" data-testid="debits-chart-section">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between gap-4">
          <div>
            <div className="overline mb-1">Expense Breakdown</div>
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">Debits by category</h3>
          </div>
          <div className="text-right">
            <div className="overline">Total debit</div>
            <div className="font-mono text-lg font-semibold text-[#EF4444]" data-testid="debits-total">
              {formatPKR(debitCats.reduce((s, c) => s + c.debit, 0))}
            </div>
          </div>
        </div>
        <div className="p-6 h-80">
          {debitCats.length === 0 ? (
            <div className="h-full flex items-center justify-center text-sm text-[#6B7280]" data-testid="debits-empty">
              No debit entries in this range.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={debitCats} margin={{ top: 28, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#F3F4F6" vertical={false} />
                <XAxis dataKey="name" stroke="#6B7280" fontSize={11} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis stroke="#6B7280" fontSize={11} tickFormatter={compactPKR} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "#FEE2E2" }} />
                <Bar dataKey="debit" name="Debit" fill="#EF4444">
                  <LabelList dataKey="debit" position="top" formatter={compactPKR} fontSize={11} fontWeight={600} fill="#EF4444" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* Net flow per party — full width */}
      <section className="border border-[#E5E7EB] bg-white mb-8" data-pdf-hide="true" data-testid="parties-chart-section">
        <div className="px-6 py-4 border-b border-[#E5E7EB]">
          <div className="overline mb-1">Top Parties</div>
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">Net flow per party</h3>
        </div>
        <div className="p-2">
          {parties.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-sm text-[#6B7280]" data-testid="parties-report-empty">No data.</div>
          ) : (
            <ul className="divide-y divide-[#E5E7EB]">
              {parties.map((p) => (
                <li key={p.name} className="px-6 py-4 flex items-center justify-between" data-testid={`party-report-${p.name}`}>
                  <div>
                    <div className="text-sm font-medium text-[#111827]">{p.name}</div>
                    <div className="text-xs text-[#6B7280] mt-0.5">
                      <span className="text-[#10B981]">+{formatPKR(p.credit)}</span> · <span className="text-[#EF4444]">−{formatPKR(p.debit)}</span>
                    </div>
                  </div>
                  <div className={`font-mono text-sm font-semibold ${p.net >= 0 ? "text-[#10B981]" : "text-[#EF4444]"}`}>
                    {p.net >= 0 ? "+" : "−"} {formatPKR(Math.abs(p.net))}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
      {/* Outstanding Dues — Receivables & Payables per Party */}
      <section className="border border-[#E5E7EB] bg-white mb-8" data-testid="dues-section">
        <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between gap-4">
          <div>
            <div className="overline mb-1">Outstanding Dues</div>
            <h3 className="font-display text-lg font-semibold text-[#0F172A]">Receivables & Payables by Party</h3>
          </div>
          <div className="flex gap-6 text-right">
            <div>
              <div className="overline">Receivable</div>
              <div className="font-mono text-lg font-semibold text-[#2563EB]" data-testid="dues-total-receivable">
                {formatPKR(dues.total_receivable || 0)}
              </div>
            </div>
            <div>
              <div className="overline">Payable</div>
              <div className="font-mono text-lg font-semibold text-[#EF4444]" data-testid="dues-total-payable">
                {formatPKR(dues.total_payable || 0)}
              </div>
            </div>
            <div>
              <div className="overline">Net</div>
              <div className={`font-mono text-lg font-semibold ${(dues.net_dues || 0) >= 0 ? "text-[#2563EB]" : "text-[#EF4444]"}`} data-testid="dues-net">
                {formatPKR(dues.net_dues || 0)}
              </div>
            </div>
          </div>
        </div>
        <div data-testid="dues-table-wrapper">
          {(!dues.rows || dues.rows.length === 0) ? (
            <div className="px-6 py-16 text-center text-sm text-[#6B7280]" data-testid="dues-empty">
              No outstanding dues. All entries fully settled.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                  <th className="text-left px-6 py-3 overline">Party</th>
                  <th className="text-left px-4 py-3 overline">Type</th>
                  <th className="text-right px-4 py-3 overline text-[#2563EB]">Receivable</th>
                  <th className="text-right px-4 py-3 overline text-[#EF4444]">Payable</th>
                  <th className="text-right px-4 py-3 overline">Net</th>
                  <th className="text-right px-6 py-3 overline">Entries</th>
                </tr>
              </thead>
              <tbody>
                {dues.rows.map((d, i) => (
                  <tr key={d.party_id || `none-${i}`} className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]" data-testid={`dues-row-${i}`}>
                    <td className="px-6 py-3 font-medium text-[#111827]">{d.name}</td>
                    <td className="px-4 py-3 text-xs">
                      {d.type && (
                        <span className="inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-[#F3F4F6] text-[#4B5563]">
                          {d.type}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-[#2563EB]">
                      {d.receivable > 0 ? formatPKR(d.receivable) : <span className="text-[#D1D5DB]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold text-[#EF4444]">
                      {d.payable > 0 ? formatPKR(d.payable) : <span className="text-[#D1D5DB]">—</span>}
                    </td>
                    <td className={`px-4 py-3 text-right font-mono font-semibold ${d.net >= 0 ? "text-[#2563EB]" : "text-[#EF4444]"}`}>
                      {formatPKR(d.net)}
                    </td>
                    <td className="px-6 py-3 text-right text-xs text-[#6B7280] font-mono">{d.entries_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      </div>
    </div>
  );
}
